'use client';

import { createClient } from '@/lib/supabase/client';
import {
  UserProfile, MemberProfile, Conversation, Message, Report, AdminNote, AuditLogEntry,
  toUserProfile, toMemberProfile, toConversation, toMessage, toReport, toAdminNote, toAuditLogEntry,
  PreviewProfile, toPreviewProfile,
  IssueReport, toIssueReport,
} from './types';

// Re-export types for backward compat
export type { UserProfile, MemberProfile, Conversation, Message, Report, AdminNote, AuditLogEntry, PreviewProfile, IssueReport };

// ─── JSN Supabase Service ─────────────────────────────────────────────────────

/**
 * Convert a base64 data URL to a Blob without using fetch().
 * fetch(dataUrl) can throw NetworkError in some browsers / CSP environments,
 * especially on repeated calls with large payloads.
 */
function dataUrlToBlob(dataUrl: string): Blob {
  const [header, base64] = dataUrl.split(',');
  const mimeMatch = header.match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
}

export const jsnService = {

  // ── Auth helpers ────────────────────────────────────────────────────────

  async getCurrentUserId(): Promise<string | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    return user?.id ?? null;
  },

  async getCurrentUserProfile(): Promise<UserProfile | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    const { data } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    return data ? toUserProfile(data as Record<string, unknown>) : null;
  },

  async getCurrentMemberProfile(): Promise<MemberProfile | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    const { data } = await supabase
      .from('member_profiles')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();
    return data ? toMemberProfile(data as Record<string, unknown>) : null;
  },

  // ── Account Repair / Consistency Check ─────────────────────────────────

  /**
   * Checks if a Supabase Auth user has matching user_profiles and member_profiles rows.
   * Returns the repair status and what's missing.
   */
  async checkAccountConsistency(userId: string): Promise<{
    hasAuthUser: boolean;
    hasUserProfile: boolean;
    hasMemberProfile: boolean;
    userProfile: UserProfile | null;
    memberProfile: MemberProfile | null;
  }> {
    const supabase = createClient();
    const { data: up } = await supabase
      .from('user_profiles').select('*').eq('id', userId)
      .maybeSingle();
    const { data: mp } = await supabase
      .from('member_profiles').select('*').eq('user_id', userId)
      .maybeSingle();
    return {
      hasAuthUser: true,
      hasUserProfile: !!up,
      hasMemberProfile: !!mp,
      userProfile: up ? toUserProfile(up as Record<string, unknown>) : null,
      memberProfile: mp ? toMemberProfile(mp as Record<string, unknown>) : null,
    };
  },

  /**
   * Repairs a missing/incomplete user_profiles row for an existing auth user.
   * Safe: does not overwrite complete existing profiles, does not make users admin.
   */
  async repairUserProfile(userId: string, email: string, displayName?: string): Promise<{ success: boolean; error?: string }> {
    const supabase = createClient();
    try {
      const { data: existing } = await supabase
        .from('user_profiles')
        .select('id, role, membership_tier')
        .eq('id', userId)
        .maybeSingle();

      if (existing) {
        // Profile exists — only fill in missing fields, never overwrite role/tier
        return { success: true };
      }

      const initials = (displayName || email.split('@')[0])
        .split(' ')
        .map((n: string) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);

      const { error } = await supabase
        .from('user_profiles')
        .insert({
          id: userId,
          email,
          display_name: displayName || email.split('@')[0],
          role: 'user',
          membership_tier: 'free',
          avatar_initials: initials,
          avatar_color: '#750000',
          joined_date: new Date().toISOString().split('T')[0],
          email_verified: false,
          account_status: 'active',
        });

      if (error) return { success: false, error: error.message };
      return { success: true };
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Unknown error' };
    }
  },

  /**
   * Repairs a missing member_profiles row for a user that has user_profiles.
   * Safe: does not overwrite existing member profiles.
   */
  async repairMemberProfile(userId: string, displayName: string): Promise<{ success: boolean; error?: string }> {
    const supabase = createClient();
    try {
      const { data: existing } = await supabase
        .from('member_profiles')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();

      if (existing) return { success: true };

      const initials = displayName
        .split(' ')
        .map((n: string) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);

      const { error } = await supabase
        .from('member_profiles')
        .insert({
          user_id: userId,
          display_name: displayName,
          age: 18,
          gender: '',
          us_state: '',
          kenyan_county: '',
          faith: '',
          intention: '',
          interests: [],
          bio: '',
          profile_status: 'new',
          avatar_initials: initials,
          avatar_color: '#750000',
          joined_date: new Date().toISOString().split('T')[0],
          last_active: new Date().toISOString().split('T')[0],
          profile_complete: 0,
          photo_url: null,
          photo_status: 'none',
          is_seed: false,
        });

      if (error) return { success: false, error: error.message };
      return { success: true };
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Unknown error' };
    }
  },

  /**
   * Determines what registration step a user needs to complete next.
   * Used to route users to the correct completion step after login.
   *
   * Phase 2: email_verified is now a hard gate enforced at the screen level
   * via Supabase Auth email_confirmed_at. Phone verification remains Phase 2 (SMS).
   */
  getIncompleteRegistrationStep(userProfile: UserProfile, memberProfile: MemberProfile | null): string | null {
    // email_verification is now enforced at the screen/route level via Supabase Auth email_confirmed_at.
    // Do not re-check it here to avoid double-gating.
    // if (!userProfile.phoneVerified) return 'phone_verification';  // Phase 2: re-enable after SMS provider is configured
    if (!memberProfile) return 'profile_setup';
    if (!memberProfile.photoUrl) return 'photo_upload';
    if (memberProfile.photoStatus === 'pending') return 'photo_pending';
    if (memberProfile.photoStatus === 'rejected') return 'photo_rejected';
    return null;
  },

  // ── Registration ────────────────────────────────────────────────────────

  async registerUser(params: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    gender: string;
    usState: string;
    kenyanCounty: string;
    intention: string;
    faith: string;
    bio: string;
    dateOfBirth: string;
    interests: string[];
    photoUrl: string | null;
    phoneNumber?: string;
    smsConsent?: boolean;
  }): Promise<{ success: boolean; error?: string; userId?: string; resumeSetup?: boolean }> {
    // All signup logic — signUp(), user_profiles update, member_profiles creation —
    // is handled server-side by /api/auth/register.
    // The server derives userId from Supabase's signUp() response directly;
    // no userId is ever supplied by or trusted from the browser.
    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: params.email,
          password: params.password,
          firstName: params.firstName,
          lastName: params.lastName,
          gender: params.gender,
          usState: params.usState,
          kenyanCounty: params.kenyanCounty,
          intention: params.intention,
          faith: params.faith,
          bio: params.bio,
          dateOfBirth: params.dateOfBirth,
          interests: params.interests,
          photoUrl: params.photoUrl ?? null,
          phoneNumber: params.phoneNumber ?? null,
          smsConsent: params.smsConsent === true,
        }),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        return {
          success: false,
          error: json?.error ?? 'Registration failed. Please try again.',
          ...(json?.resumeSetup ? { resumeSetup: true } : {}),
        };
      }

      return { success: true, userId: json.userId };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Registration failed. Please try again.';
      return { success: false, error: message };
    }
  },

  async markEmailVerified(userId: string): Promise<void> {
    const supabase = createClient();
    await supabase
      .from('user_profiles')
      .update({ email_verified: true })
      .eq('id', userId);
  },

  async markPhoneVerified(userId: string, phoneNumber: string): Promise<void> {
    const supabase = createClient();
    await supabase
      .from('user_profiles')
      .update({
        phone_number: phoneNumber,
        phone_verified: true,
        phone_verified_at: new Date().toISOString(),
      })
      .eq('id', userId);
  },

  // ── Discover ────────────────────────────────────────────────────────────

  async getDiscoverProfiles(currentUserId: string): Promise<MemberProfile[]> {
    const supabase = createClient();

    // Fetch blocks, passes, and suspended users in parallel
    const [blocksRes, passesRes, suspendedRes] = await Promise.all([
      supabase
        .from('blocks')
        .select('blocked_profile_id')
        .eq('blocker_user_id', currentUserId),
      supabase
        .from('passes')
        .select('passed_profile_id')
        .eq('passer_user_id', currentUserId),
      supabase
        .from('user_profiles')
        .select('id')
        .in('account_status', ['suspended', 'deleted', 'deactivated']),
    ]);

    const blockedIds = (blocksRes.data || []).map((b: Record<string, unknown>) => b.blocked_profile_id as string);
    const passedIds = (passesRes.data || []).map((p: Record<string, unknown>) => p.passed_profile_id as string);
    const excludedUserIds = (suspendedRes.data || []).map((u: Record<string, unknown>) => u.id as string);

    // Omit photo_url on Discover list — full photos are large base64 blobs and slow the grid.
    const discoverSelect =
      'id, user_id, display_name, age, gender, us_state, kenyan_county, faith, intention, interests, bio, profile_status, avatar_initials, avatar_color, photo_url, joined_date, last_active, profile_complete, photo_status, is_seed, created_at, updated_at';

    let query = supabase
      .from('member_profiles')
      .select(discoverSelect)
      .eq('photo_status', 'approved')
      .gte('age', 21)
      .not('gender', 'is', null)
      .neq('gender', '')
      .neq('user_id', currentUserId);

    if (blockedIds.length > 0) {
      query = query.not('id', 'in', `(${blockedIds.join(',')})`);
    }

    if (passedIds.length > 0) {
      query = query.not('id', 'in', `(${passedIds.join(',')})`);
    }

    if (excludedUserIds.length > 0) {
      query = query.not('user_id', 'in', `(${excludedUserIds.join(',')})`);
    }

    const { data } = await query;
    if (!data) return [];

    return data.map((p: Record<string, unknown>) => toMemberProfile(p));
  },

  // ── Likes ───────────────────────────────────────────────────────────────

  async getLikedProfileIds(userId: string): Promise<string[]> {
    const supabase = createClient();
    const { data } = await supabase
      .from('likes')
      .select('liked_profile_id')
      .eq('liker_user_id', userId);
    return (data || []).map((l: Record<string, unknown>) => l.liked_profile_id as string);
  },

  async getLikedByProfileIds(userId: string): Promise<string[]> {
    const supabase = createClient();
    const { data: myProfile } = await supabase
      .from('member_profiles')
      .select('id')
      .eq('user_id', userId)
      .maybeSingle();
    if (!myProfile) return [];

    const { data } = await supabase
      .from('likes')
      .select('liker_user_id')
      .eq('liked_profile_id', (myProfile as Record<string, unknown>).id as string);

    if (!data || data.length === 0) return [];

    const likerUserIds = (data as Record<string, unknown>[]).map(l => l.liker_user_id as string);
    const { data: likerProfiles } = await supabase
      .from('member_profiles')
      .select('id')
      .in('user_id', likerUserIds);

    return (likerProfiles || []).map((p: Record<string, unknown>) => p.id as string);
  },

  async toggleLike(userId: string, profileId: string): Promise<boolean> {
    const supabase = createClient();
    const { data: existing } = await supabase
      .from('likes')
      .select('id')
      .eq('liker_user_id', userId)
      .eq('liked_profile_id', profileId)
      .maybeSingle();

    if (existing) {
      await supabase.from('likes').delete()
        .eq('liker_user_id', userId)
        .eq('liked_profile_id', profileId);
      return false;
    } else {
      await supabase.from('likes').insert({
        liker_user_id: userId,
        liked_profile_id: profileId,
      });
      return true;
    }
  },

  async likeBack(userId: string, profileId: string): Promise<void> {
    const supabase = createClient();
    await supabase.from('likes').upsert({
      liker_user_id: userId,
      liked_profile_id: profileId,
    }, { onConflict: 'liker_user_id,liked_profile_id' });
  },

  // ── Passes ──────────────────────────────────────────────────────────────

  async getPassedProfileIds(userId: string): Promise<string[]> {
    const supabase = createClient();
    const { data } = await supabase
      .from('passes')
      .select('passed_profile_id')
      .eq('passer_user_id', userId);
    return (data || []).map((p: Record<string, unknown>) => p.passed_profile_id as string);
  },

  async passProfile(userId: string, profileId: string): Promise<void> {
    const supabase = createClient();
    await supabase.from('passes').upsert({
      passer_user_id: userId,
      passed_profile_id: profileId,
    }, { onConflict: 'passer_user_id,passed_profile_id' });
  },

  // ── Blocks ──────────────────────────────────────────────────────────────

  async getBlockedProfileIds(userId: string): Promise<string[]> {
    const supabase = createClient();
    const { data } = await supabase
      .from('blocks')
      .select('blocked_profile_id')
      .eq('blocker_user_id', userId);
    return (data || []).map((b: Record<string, unknown>) => b.blocked_profile_id as string);
  },

  async blockUser(userId: string, profileId: string): Promise<void> {
    const supabase = createClient();
    await supabase.from('blocks').upsert({
      blocker_user_id: userId,
      blocked_profile_id: profileId,
    }, { onConflict: 'blocker_user_id,blocked_profile_id' });
  },

  // ── Reports ─────────────────────────────────────────────────────────────

  async reportUser(userId: string, profileId: string, reason: string, details: string): Promise<void> {
    const supabase = createClient();
    await supabase.from('reports').insert({
      reporter_user_id: userId,
      reported_profile_id: profileId,
      reason,
      details,
      status: 'pending',
    });
  },

  async getAllReports(): Promise<Report[]> {
    const supabase = createClient();
    const { data } = await supabase
      .from('reports')
      .select('*')
      .order('created_at', { ascending: false });
    return (data || []).map((r: Record<string, unknown>) => toReport(r));
  },

  async updateReportStatus(reportId: string, status: string, adminNote?: string): Promise<void> {
    const supabase = createClient();
    const updates: Record<string, unknown> = { status, updated_at: new Date().toISOString() };
    if (adminNote !== undefined) updates.admin_note = adminNote;
    await supabase.from('reports').update(updates).eq('id', reportId);
  },

  // ── Messages ────────────────────────────────────────────────────────────

  async getConversations(userId: string): Promise<Conversation[]> {
    const supabase = createClient();
    const { data } = await supabase
      .from('conversations')
      .select('*')
      .or(`participant_one_id.eq.${userId},participant_two_id.eq.${userId}`)
      .order('updated_at', { ascending: false });
    return (data || []).map((c: Record<string, unknown>) => toConversation(c));
  },

  async getMessages(conversationId: string): Promise<Message[]> {
    const supabase = createClient();
    const { data } = await supabase
      .from('messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true });
    return (data || []).map((m: Record<string, unknown>) => toMessage(m));
  },

  async sendMessage(conversationId: string, senderId: string, text: string): Promise<Message | null> {
    const supabase = createClient();
    const { data } = await supabase
      .from('messages')
      .insert({ conversation_id: conversationId, sender_id: senderId, text, is_read: false })
      .select()
      .single();

    if (data) {
      await supabase.from('conversations').update({
        last_message: text,
        last_message_time: new Date().toISOString(),
      }).eq('id', conversationId);
    }

    return data ? toMessage(data as Record<string, unknown>) : null;
  },

  async markConversationRead(conversationId: string, userId: string): Promise<void> {
    const supabase = createClient();
    const { data: conv } = await supabase
      .from('conversations')
      .select('participant_one_id, participant_two_id')
      .eq('id', conversationId)
      .single();

    if (!conv) return;
    const c = conv as Record<string, unknown>;
    const field = c.participant_one_id === userId ? 'unread_count_one' : 'unread_count_two';
    await supabase.from('conversations').update({ [field]: 0 }).eq('id', conversationId);
    await supabase.from('messages').update({ is_read: true })
      .eq('conversation_id', conversationId)
      .neq('sender_id', userId);
  },

  // ── Profile management ──────────────────────────────────────────────────

  async updateMemberProfile(userId: string, updates: Partial<{
    displayName: string;
    bio: string;
    usState: string;
    kenyanCounty: string;
    intention: string;
    faith: string;
    interests: string[];
  }>): Promise<{ error: string | null }> {
    const supabase = createClient();
    const dbUpdates: Record<string, unknown> = {};
    if (updates.displayName !== undefined) dbUpdates.display_name = updates.displayName;
    if (updates.bio !== undefined) dbUpdates.bio = updates.bio;
    if (updates.usState !== undefined) dbUpdates.us_state = updates.usState;
    if (updates.kenyanCounty !== undefined) dbUpdates.kenyan_county = updates.kenyanCounty;
    if (updates.intention !== undefined) dbUpdates.intention = updates.intention;
    if (updates.faith !== undefined) dbUpdates.faith = updates.faith;
    if (updates.interests !== undefined) dbUpdates.interests = updates.interests;

    const { error } = await supabase.from('member_profiles').update(dbUpdates).eq('user_id', userId);

    if (error) {
      console.error('updateMemberProfile error:', error.message);
      return { error: error.message };
    }

    if (updates.displayName) {
      const initials = updates.displayName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
      await supabase.from('user_profiles').update({
        display_name: updates.displayName,
        avatar_initials: initials,
      }).eq('id', userId);
      await supabase.from('member_profiles').update({
        avatar_initials: initials,
      }).eq('user_id', userId);
    }

    return { error: null };
  },

  async resubmitPhoto(userId: string, photoUrl: string): Promise<void> {
    const supabase = createClient();
    await supabase.from('member_profiles').update({
      photo_url: photoUrl,
      photo_status: 'pending',
      photo_rejection_reason: null,
    }).eq('user_id', userId);
  },

  // ── Admin ───────────────────────────────────────────────────────────────

  async getAllUserProfiles(): Promise<UserProfile[]> {
    const supabase = createClient();
    const { data } = await supabase
      .from('user_profiles')
      .select('*')
      .neq('role', 'admin')
      .order('created_at', { ascending: false });
    return (data || []).map((u: Record<string, unknown>) => toUserProfile(u));
  },

  async getAllMemberProfiles(): Promise<MemberProfile[]> {
    const supabase = createClient();
    const { data } = await supabase
      .from('member_profiles')
      .select('*')
      .order('created_at', { ascending: false });
    return (data || []).map((p: Record<string, unknown>) => toMemberProfile(p));
  },

  async getPendingPhotoProfiles(): Promise<MemberProfile[]> {
    const supabase = createClient();
    const { data } = await supabase
      .from('member_profiles')
      .select('*')
      .eq('photo_status', 'pending')
      .eq('is_seed', false)
      .order('created_at', { ascending: true });
    return (data || []).map((p: Record<string, unknown>) => toMemberProfile(p));
  },

  async approvePhoto(userId: string, adminId?: string): Promise<void> {
    const supabase = createClient();
    // Approve the photo and ensure profile_complete is at least 90 so the
    // member appears in Discover with a reasonable completeness score.
    // Also clear any rejection reason from a previous rejection cycle.
    const { data: existing } = await supabase
      .from('member_profiles')
      .select('profile_complete')
      .eq('user_id', userId)
      .maybeSingle();
    const currentComplete = (existing as Record<string, unknown> | null)?.profile_complete as number | undefined;
    await supabase.from('member_profiles').update({
      photo_status: 'approved',
      photo_rejection_reason: null,
      // Bump profile_complete to at least 90 on approval so the member
      // is not buried at the bottom of completeness-sorted results.
      profile_complete: Math.max(currentComplete ?? 0, 90),
    }).eq('user_id', userId);
    if (adminId) {
      await jsnService.addAuditLog(adminId, userId, 'photo_approved', 'Admin approved profile photo');
    }
    // Clear the in-memory Discover prefetch cache so the next Discover load
    // fetches fresh data that includes this newly approved member.
    // Import is at the top of this file via the module-level singleton.
    try {
      const { clearDiscoverCache } = await import('@/lib/discoverPrefetch');
      clearDiscoverCache();
    } catch {
      // Non-critical — Discover will still refresh on next full load
    }
  },

  async rejectPhoto(userId: string, reason: string, adminId?: string): Promise<void> {
    const supabase = createClient();
    await supabase.from('member_profiles').update({
      photo_status: 'rejected',
      photo_rejection_reason: reason,
    }).eq('user_id', userId);
    if (adminId) {
      await jsnService.addAuditLog(adminId, userId, 'photo_rejected', `Admin rejected photo: ${reason}`);
    }
  },

  async resetPhotoToPending(userId: string, adminId?: string): Promise<void> {
    const supabase = createClient();
    await supabase.from('member_profiles').update({
      photo_status: 'pending',
      photo_rejection_reason: null,
    }).eq('user_id', userId);
    if (adminId) {
      await jsnService.addAuditLog(adminId, userId, 'photo_reset_pending', 'Admin reset photo to pending');
    }
  },

  async grantPremium(userId: string, plan: string, adminId?: string): Promise<void> {
    const supabase = createClient();
    const planMonths: Record<string, number> = { flex: 1, value: 3, best: 6, elite: 12 };
    const months = planMonths[plan] ?? 1;
    const expiry = new Date();
    expiry.setMonth(expiry.getMonth() + months);
    await supabase.from('user_profiles').update({
      membership_tier: 'premium',
      membership_plan: plan,
      membership_start_date: new Date().toISOString().split('T')[0],
      membership_expiry_date: expiry.toISOString().split('T')[0],
    }).eq('id', userId);
    if (adminId) {
      await jsnService.addAuditLog(adminId, userId, 'premium_granted', `Admin granted ${plan} premium`);
    }
  },

  async revokePremium(userId: string, adminId?: string): Promise<void> {
    const supabase = createClient();
    await supabase.from('user_profiles').update({
      membership_tier: 'free',
      membership_plan: null,
      membership_start_date: null,
      membership_expiry_date: null,
    }).eq('id', userId);
    if (adminId) {
      await jsnService.addAuditLog(adminId, userId, 'premium_revoked', 'Admin revoked premium');
    }
  },

  async extendPremium(userId: string, months: number, adminId?: string): Promise<void> {
    const supabase = createClient();
    const { data: up } = await supabase.from('user_profiles').select('membership_expiry_date').eq('id', userId).single();
    const currentExpiry = up && (up as Record<string, unknown>).membership_expiry_date
      ? new Date((up as Record<string, unknown>).membership_expiry_date as string)
      : new Date();
    currentExpiry.setMonth(currentExpiry.getMonth() + months);
    await supabase.from('user_profiles').update({
      membership_expiry_date: currentExpiry.toISOString().split('T')[0],
    }).eq('id', userId);
    if (adminId) {
      await jsnService.addAuditLog(adminId, userId, 'premium_extended', `Admin extended premium by ${months} month(s)`);
    }
  },

  async suspendUser(userId: string, adminId?: string, reason?: string, notes?: string): Promise<void> {
    const supabase = createClient();
    await supabase.from('user_profiles').update({
      account_status: 'suspended',
      enforcement_reason: reason || null,
      enforcement_type: 'suspension',
      enforcement_date: new Date().toISOString(),
      enforcement_notes: notes || null,
      deactivation_delete_after: null,
      corrected_profile_pending_review: false,
    }).eq('id', userId);
    if (adminId) {
      await jsnService.addAuditLog(adminId, userId, 'user_suspended', reason || 'Admin suspended user');
    }
  },

  async adminBlockUser(userId: string, adminId: string): Promise<void> {
    const supabase = createClient();
    await supabase.from('user_profiles').update({ account_status: 'blocked' }).eq('id', userId);
    await jsnService.addAuditLog(adminId, userId, 'user_blocked', 'Admin blocked user account');
  },

  async reactivateUser(userId: string, adminId?: string): Promise<void> {
    const supabase = createClient();
    await supabase.from('user_profiles').update({
      account_status: 'active',
      enforcement_reason: null,
      enforcement_type: null,
      enforcement_date: null,
      enforcement_notes: null,
      deactivation_delete_after: null,
      corrected_profile_pending_review: false,
    }).eq('id', userId);
    if (adminId) {
      await jsnService.addAuditLog(adminId, userId, 'user_reactivated', 'Admin reactivated user');
    }
  },

  async deactivateUser(userId: string, adminId?: string, reason?: string, notes?: string): Promise<void> {
    const supabase = createClient();
    const deleteAfter = new Date();
    deleteAfter.setDate(deleteAfter.getDate() + 30);
    await supabase.from('user_profiles').update({
      account_status: 'deactivated',
      enforcement_reason: reason || null,
      enforcement_type: 'deactivation',
      enforcement_date: new Date().toISOString(),
      enforcement_notes: notes || null,
      deactivation_delete_after: deleteAfter.toISOString(),
      corrected_profile_pending_review: false,
    }).eq('id', userId);
    if (adminId) {
      await jsnService.addAuditLog(adminId, userId, 'user_deactivated', reason || 'Admin deactivated user');
    }
  },

  async adminKeepSuspended(userId: string, adminId: string, notes?: string): Promise<void> {
    const supabase = createClient();
    if (notes) {
      await supabase.from('user_profiles').update({
        enforcement_notes: notes,
        corrected_profile_pending_review: false,
      }).eq('id', userId);
    } else {
      await supabase.from('user_profiles').update({
        corrected_profile_pending_review: false,
      }).eq('id', userId);
    }
    await jsnService.addAuditLog(adminId, userId, 'suspension_kept', notes || 'Admin reviewed corrections and kept suspension');
  },

  async adminSoftDeleteUser(userId: string, adminId?: string): Promise<void> {
    const supabase = createClient();
    // Soft delete: mark as deleted, keep data
    await supabase.from('user_profiles').update({ account_status: 'deleted' }).eq('id', userId);
    if (adminId) {
      await jsnService.addAuditLog(adminId, userId, 'user_deleted', 'Admin soft-deleted user account');
    }
  },

  async adminDeleteUser(userId: string): Promise<void> {
    const supabase = createClient();
    const { data: mp } = await supabase
      .from('member_profiles')
      .select('id')
      .eq('user_id', userId)
      .maybeSingle();

    if (mp) {
      const profileId = (mp as Record<string, unknown>).id as string;
      await supabase.from('likes').delete().or(`liker_user_id.eq.${userId},liked_profile_id.eq.${profileId}`);
      await supabase.from('passes').delete().or(`passer_user_id.eq.${userId},passed_profile_id.eq.${profileId}`);
      await supabase.from('blocks').delete().or(`blocker_user_id.eq.${userId},blocked_profile_id.eq.${profileId}`);
      await supabase.from('reports').delete().or(`reporter_user_id.eq.${userId},reported_profile_id.eq.${profileId}`);
      await supabase.from('member_profiles').delete().eq('id', profileId);
    }

    const { data: convs } = await supabase
      .from('conversations')
      .select('id')
      .or(`participant_one_id.eq.${userId},participant_two_id.eq.${userId}`);

    if (convs && convs.length > 0) {
      const convIds = (convs as Record<string, unknown>[]).map(c => c.id as string);
      await supabase.from('messages').delete().in('conversation_id', convIds);
      await supabase.from('conversations').delete().in('id', convIds);
    }

    await supabase.from('premium_subscriptions').delete().eq('user_id', userId);
    await supabase.from('admin_notes').delete().eq('target_user_id', userId);
    await supabase.from('user_profiles').delete().eq('id', userId);

    // Hard delete: also remove the Supabase Auth user so the email can be reused.
    // This requires the service role key (server-side only).
    try {
      await fetch('/api/auth/delete-auth-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      });
    } catch (e) {
      // Non-fatal: JSN profile rows are already deleted. Auth user cleanup failed
      // (likely service role not configured). Log for visibility.
      console.warn('[adminDeleteUser] Could not delete Supabase Auth user:', e);
    }
  },

  async selfDeleteAccount(userId: string): Promise<void> {
    await jsnService.adminDeleteUser(userId);
  },

  async sendPasswordResetEmail(email: string): Promise<{ success: boolean; error?: string }> {
    const supabase = createClient();
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://us.jamuhurisingles.com';
    // redirectTo must go through /auth/callback?type=recovery so the PKCE code
    // is forwarded to /forgot-password without being consumed in callback.
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${siteUrl}/auth/callback?type=recovery`,
    });
    if (error) return { success: false, error: error.message };
    return { success: true };
  },

  // ── Admin Notes ─────────────────────────────────────────────────────────

  async getAdminNotes(targetUserId: string): Promise<AdminNote[]> {
    const supabase = createClient();
    const { data } = await supabase
      .from('admin_notes')
      .select('*')
      .eq('target_user_id', targetUserId)
      .order('created_at', { ascending: false });
    return (data || []).map((n: Record<string, unknown>) => toAdminNote(n));
  },

  async addAdminNote(adminUserId: string, targetUserId: string, note: string): Promise<void> {
    const supabase = createClient();
    await supabase.from('admin_notes').insert({
      admin_user_id: adminUserId,
      target_user_id: targetUserId,
      note,
    });
  },

  async deleteAdminNote(noteId: string): Promise<void> {
    const supabase = createClient();
    await supabase.from('admin_notes').delete().eq('id', noteId);
  },

  // ── Audit Log ───────────────────────────────────────────────────────────

  async addAuditLog(adminUserId: string, targetUserId: string | null, action: string, details?: string, reason?: string): Promise<void> {
    const supabase = createClient();
    await supabase.from('admin_audit_log').insert({
      admin_user_id: adminUserId,
      target_user_id: targetUserId,
      action,
      details: details || null,
      reason: reason || null,
    });
  },

  async getAuditLog(targetUserId?: string, limit = 100): Promise<AuditLogEntry[]> {
    const supabase = createClient();
    let query = supabase
      .from('admin_audit_log')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);
    if (targetUserId) {
      query = query.eq('target_user_id', targetUserId);
    }
    const { data } = await query;
    return (data || []).map((e: Record<string, unknown>) => toAuditLogEntry(e));
  },

  // ── Admin Blocks Management ─────────────────────────────────────────────

  async getAllBlocks(): Promise<Array<{
    id: string;
    blockerUserId: string;
    blockedProfileId: string;
    createdAt: string;
    blockerProfile?: UserProfile | null;
    blockedMemberProfile?: MemberProfile | null;
  }>> {
    const supabase = createClient();
    const { data } = await supabase
      .from('blocks')
      .select('*')
      .order('created_at', { ascending: false });
    return (data || []).map((b: Record<string, unknown>) => ({
      id: b.id as string,
      blockerUserId: b.blocker_user_id as string,
      blockedProfileId: b.blocked_profile_id as string,
      createdAt: b.created_at as string,
    }));
  },

  async adminUnblock(blockerUserId: string, blockedProfileId: string, adminId?: string): Promise<void> {
    const supabase = createClient();
    await supabase.from('blocks')
      .delete()
      .eq('blocker_user_id', blockerUserId)
      .eq('blocked_profile_id', blockedProfileId);
    if (adminId) {
      await jsnService.addAuditLog(adminId, blockerUserId, 'block_removed', `Admin removed block on profile ${blockedProfileId}`);
    }
  },

  // ── Admin Repair / Debug ────────────────────────────────────────────────

  async getOrphanedProfiles(): Promise<{
    missingUserProfile: Array<{ userId: string; email: string }>;
    missingMemberProfile: Array<{ userId: string; displayName: string; email: string }>;
  }> {
    const supabase = createClient();

    // Find member_profiles with no matching user_profile
    const { data: allMemberProfiles } = await supabase
      .from('member_profiles')
      .select('user_id, display_name')
      .eq('is_seed', false);

    const { data: allUserProfiles } = await supabase
      .from('user_profiles')
      .select('id, email, display_name')
      .neq('role', 'admin');

    const userProfileIds = new Set((allUserProfiles || []).map((u: Record<string, unknown>) => u.id as string));
    const memberProfileUserIds = new Set((allMemberProfiles || []).map((m: Record<string, unknown>) => m.user_id as string));

    // User profiles without member profiles
    const missingMemberProfile = (allUserProfiles || [])
      .filter((u: Record<string, unknown>) => !memberProfileUserIds.has(u.id as string))
      .map((u: Record<string, unknown>) => ({
        userId: u.id as string,
        displayName: u.display_name as string,
        email: u.email as string,
      }));

    return {
      missingUserProfile: [],
      missingMemberProfile,
    };
  },

  async repairAccount(userId: string, adminId: string): Promise<{ success: boolean; actions: string[]; error?: string }> {
    const supabase = createClient();
    const actions: string[] = [];

    try {
      const { data: up } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (!up) {
        return { success: false, actions, error: 'User profile not found. Cannot repair without base profile.' };
      }

      const userProfile = toUserProfile(up as Record<string, unknown>);

      // Check member profile
      const { data: mp } = await supabase
        .from('member_profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (!mp) {
        const repairResult = await jsnService.repairMemberProfile(userId, userProfile.displayName);
        if (repairResult.success) {
          actions.push('Created missing member profile with safe defaults');
        } else {
          actions.push(`Failed to create member profile: ${repairResult.error}`);
        }
      } else {
        actions.push('Member profile already exists — no repair needed');
      }

      // Fix account_status if stuck
      if (userProfile.accountStatus === 'deleted' || userProfile.accountStatus === 'deactivated') {
        actions.push(`Note: Account status is "${userProfile.accountStatus}" — admin may want to reactivate`);
      }

      await jsnService.addAuditLog(adminId, userId, 'account_repaired', `Admin repaired account. Actions: ${actions.join('; ')}`);

      return { success: true, actions };
    } catch (e) {
      return { success: false, actions, error: e instanceof Error ? e.message : 'Unknown error' };
    }
  },

  async getAdminStats(): Promise<{
    totalUsers: number;
    premiumUsers: number;
    totalProfiles: number;
    pendingReports: number;
    totalReports: number;
    pendingPhotos: number;
    suspendedUsers: number;
    deactivatedUsers: number;
  }> {
    const supabase = createClient();
    const [usersRes, premiumRes, profilesRes, pendingReportsRes, totalReportsRes, pendingPhotosRes, suspendedRes, deactivatedRes] = await Promise.all([
      supabase.from('user_profiles').select('*', { count: 'exact', head: true }).neq('role', 'admin').neq('account_status', 'deleted'),
      supabase.from('user_profiles').select('*', { count: 'exact', head: true }).eq('membership_tier', 'premium').neq('role', 'admin'),
      supabase.from('member_profiles').select('*', { count: 'exact', head: true }),
      supabase.from('reports').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
      supabase.from('reports').select('*', { count: 'exact', head: true }),
      supabase.from('member_profiles').select('*', { count: 'exact', head: true }).eq('photo_status', 'pending').eq('is_seed', false),
      supabase.from('user_profiles').select('*', { count: 'exact', head: true }).eq('account_status', 'suspended'),
      supabase.from('user_profiles').select('*', { count: 'exact', head: true }).eq('account_status', 'deactivated'),
    ]);

    return {
      totalUsers: usersRes.count || 0,
      premiumUsers: premiumRes.count || 0,
      totalProfiles: profilesRes.count || 0,
      pendingReports: pendingReportsRes.count || 0,
      totalReports: totalReportsRes.count || 0,
      pendingPhotos: pendingPhotosRes.count || 0,
      suspendedUsers: suspendedRes.count || 0,
      deactivatedUsers: deactivatedRes.count || 0,
    };
  },

  // ── Premium ────────────────────────────────────────────────────

  async upgradeToPremium(userId: string, plan: string): Promise<void> {
    const supabase = createClient();
    const planMonths: Record<string, number> = { flex: 1, value: 3, best: 6, elite: 12 };
    const planAmounts: Record<string, number> = { flex: 2999, value: 7999, best: 14999, elite: 26999 };
    const months = planMonths[plan] ?? 1;
    const startDate = new Date().toISOString().split('T')[0];
    const expiry = new Date();
    expiry.setMonth(expiry.getMonth() + months);
    const endDate = expiry.toISOString().split('T')[0];

    await supabase.from('user_profiles').update({
      membership_tier: 'premium',
      membership_plan: plan,
      membership_start_date: startDate,
      membership_expiry_date: endDate,
    }).eq('id', userId);

    // Record subscription without Stripe fields
    await supabase.from('premium_subscriptions').insert({
      user_id: userId,
      plan,
      payment_status: 'succeeded',
      start_date: startDate,
      end_date: endDate,
      amount_cents: planAmounts[plan] ?? 2999,
      currency: 'usd',
    });
  },

  async getUserPremiumSubscriptions(userId: string): Promise<Array<Record<string, unknown>>> {
    const supabase = createClient();
    const { data } = await supabase
      .from('premium_subscriptions')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    return data || [];
  },

  async getUserProfileById(userId: string): Promise<UserProfile | null> {
    const supabase = createClient();
    const { data } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();
    return data ? toUserProfile(data as Record<string, unknown>) : null;
  },

  async getUserProfileByEmail(email: string): Promise<UserProfile | null> {
    const supabase = createClient();
    const { data } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('email', email)
      .maybeSingle();
    return data ? toUserProfile(data as Record<string, unknown>) : null;
  },

  async adminMarkEmailVerified(targetUserId: string, adminId: string): Promise<{ success: boolean; error?: string }> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          email_verified: true,
          email_verified_at: new Date().toISOString(),
        })
        .eq('id', targetUserId);
      if (error) return { success: false, error: error.message };
      await jsnService.addAuditLog(
        adminId,
        targetUserId,
        'mark_email_verified',
        `Admin manually marked email as verified (emergency override). Admin ID: ${adminId}`,
      );
      return { success: true };
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Unknown error' };
    }
  },

  async adminMarkPhoneVerified(targetUserId: string, adminId: string): Promise<{ success: boolean; error?: string }> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ phone_verified: true, phone_verified_at: new Date().toISOString() })
        .eq('id', targetUserId);
      if (error) return { success: false, error: error.message };
      await jsnService.addAuditLog(
        adminId,
        targetUserId,
        'mark_phone_verified',
        'Admin manually marked phone as verified (emergency override)',
      );
      return { success: true };
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Unknown error' };
    }
  },

  async adminMarkIdentityReviewed(targetUserId: string, adminId: string): Promise<{ success: boolean; error?: string }> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          identity_reviewed: true,
          identity_reviewed_at: new Date().toISOString(),
          identity_reviewed_by: adminId,
        })
        .eq('id', targetUserId);
      if (error) return { success: false, error: error.message };
      await jsnService.addAuditLog(
        adminId,
        targetUserId,
        'mark_identity_reviewed',
        'Admin manually marked identity as reviewed',
      );
      return { success: true };
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Unknown error' };
    }
  },

  // ── Preview Profiles ────────────────────────────────────────────────────

  async getVisiblePreviewProfiles(): Promise<PreviewProfile[]> {
    const supabase = createClient();
    // Omit photo_url on Discover list — demo photos are stored as huge base64 (~41MB total).
    const previewListSelect =
      'id, display_name, age, gender, us_state, kenyan_county, faith, intention, interests, bio, avatar_initials, avatar_color, profile_type, is_visible, created_at, updated_at';
    const { data, error } = await supabase
      .from('preview_profiles')
      .select(previewListSelect)
      .eq('is_visible', true)
      .order('created_at', { ascending: true });
    if (error) {
      console.error('[getVisiblePreviewProfiles] query error:', error.message, error.code);
    }
    return (data || []).map((p: Record<string, unknown>) => toPreviewProfile(p));
  },

  /**
   * Loads photo_url per preview (one row at a time).
   * A bulk select=id,photo_url times out (57014) because every row stores multi-MB base64.
   */
  async enrichVisiblePreviewPhotos(profiles: PreviewProfile[]): Promise<PreviewProfile[]> {
    if (profiles.length === 0) return profiles;

    const supabase = createClient();
    const concurrency = 4;
    const enriched = [...profiles];

    for (let i = 0; i < enriched.length; i += concurrency) {
      const batch = enriched.slice(i, i + concurrency);
      const results = await Promise.all(
        batch.map(async (profile, batchIndex) => {
          const index = i + batchIndex;
          try {
            const { data, error } = await supabase
              .from('preview_profiles')
              .select('photo_url')
              .eq('id', profile.id)
              .maybeSingle();
            if (error) {
              console.warn('[enrichVisiblePreviewPhotos] photo fetch failed:', profile.id, error.code, error.message);
              return null;
            }
            const photoUrl = (data?.photo_url as string | null) ?? null;
            if (!photoUrl) return null;
            return { index, photoUrl };
          } catch (e) {
            console.warn('[enrichVisiblePreviewPhotos] photo fetch error:', profile.id, e);
            return null;
          }
        }),
      );

      for (const result of results) {
        if (!result) continue;
        enriched[result.index] = { ...enriched[result.index], photoUrl: result.photoUrl };
      }
    }

    return enriched;
  },

  async getAllPreviewProfiles(): Promise<PreviewProfile[]> {
    try {
      // Use the admin API route which uses the service role key to bypass RLS
      const res = await fetch('/api/admin/preview-profiles', { credentials: 'include' });
      if (res.ok) {
        const json = await res.json();
        return (json.data || []).map((p: Record<string, unknown>) => toPreviewProfile(p));
      }
      console.error('[getAllPreviewProfiles] API route returned', res.status);
    } catch (fetchErr) {
      console.error('[getAllPreviewProfiles] fetch error, falling back to direct query:', fetchErr);
    }
    // Fallback: direct Supabase query (subject to RLS)
    const supabase = createClient();
    const { data, error } = await supabase
      .from('preview_profiles')
      .select('*')
      .order('created_at', { ascending: true });
    if (error) {
      console.error('[getAllPreviewProfiles] RLS/query error:', error.message, error.code);
    }
    return (data || []).map((p: Record<string, unknown>) => toPreviewProfile(p));
  },

  async getPreviewProfileById(id: string): Promise<PreviewProfile | null> {
    const supabase = createClient();
    const { data } = await supabase
      .from('preview_profiles')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    return data ? toPreviewProfile(data as Record<string, unknown>) : null;
  },

  async createPreviewProfile(params: {
    displayName: string;
    age: number;
    gender: string;
    usState: string;
    kenyanCounty: string;
    faith: string;
    intention: string;
    interests: string[];
    bio: string;
    photoUrl?: string | null;
  }): Promise<{ success: boolean; id?: string; error?: string }> {
    const initials = params.displayName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
    const colors = ['#750000', '#38761d', '#5a3a8a', '#1a6b8a', '#8a6b1a', '#008000'];
    const avatarColor = colors[Math.floor(Math.random() * colors.length)];
    try {
      const res = await fetch('/api/admin/preview-profiles', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          display_name: params.displayName,
          age: params.age,
          gender: params.gender,
          us_state: params.usState,
          kenyan_county: params.kenyanCounty,
          faith: params.faith,
          intention: params.intention,
          interests: params.interests,
          bio: params.bio,
          avatar_initials: initials,
          avatar_color: avatarColor,
          photo_url: params.photoUrl || null,
        }),
      });
      const json = await res.json();
      if (!res.ok) return { success: false, error: json.error || 'Failed to create profile' };
      return { success: true, id: json.data?.id };
    } catch (err) {
      console.error('[createPreviewProfile] fetch error:', err);
      return { success: false, error: 'Network error' };
    }
  },

  async updatePreviewProfile(id: string, params: Partial<{
    displayName: string;
    age: number;
    gender: string;
    usState: string;
    kenyanCounty: string;
    faith: string;
    intention: string;
    interests: string[];
    bio: string;
    photoUrl: string | null;
    isVisible: boolean;
  }>): Promise<{ success: boolean; error?: string }> {
    const updates: Record<string, unknown> = { id };
    if (params.displayName !== undefined) {
      updates.display_name = params.displayName;
      updates.avatar_initials = params.displayName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (params.age !== undefined) updates.age = params.age;
    if (params.gender !== undefined) updates.gender = params.gender;
    if (params.usState !== undefined) updates.us_state = params.usState;
    if (params.kenyanCounty !== undefined) updates.kenyan_county = params.kenyanCounty;
    if (params.faith !== undefined) updates.faith = params.faith;
    if (params.intention !== undefined) updates.intention = params.intention;
    if (params.interests !== undefined) updates.interests = params.interests;
    if (params.bio !== undefined) updates.bio = params.bio;
    if (params.photoUrl !== undefined) updates.photo_url = params.photoUrl;
    if (params.isVisible !== undefined) updates.is_visible = params.isVisible;
    try {
      const res = await fetch('/api/admin/preview-profiles', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      const json = await res.json();
      if (!res.ok) return { success: false, error: json.error || 'Failed to update profile' };
      return { success: true };
    } catch (err) {
      console.error('[updatePreviewProfile] fetch error:', err);
      return { success: false, error: 'Network error' };
    }
  },

  async deletePreviewProfile(id: string): Promise<{ success: boolean; error?: string }> {
    try {
      const res = await fetch(`/api/admin/preview-profiles?id=${encodeURIComponent(id)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const json = await res.json();
      if (!res.ok) return { success: false, error: json.error || 'Failed to delete profile' };
      return { success: true };
    } catch (err) {
      console.error('[deletePreviewProfile] fetch error:', err);
      return { success: false, error: 'Network error' };
    }
  },

  async togglePreviewProfileVisibility(id: string, isVisible: boolean): Promise<{ success: boolean; error?: string }> {
    try {
      const res = await fetch('/api/admin/preview-profiles', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, is_visible: isVisible }),
      });
      const json = await res.json();
      if (!res.ok) return { success: false, error: json.error || 'Failed to toggle visibility' };
      return { success: true };
    } catch (err) {
      console.error('[togglePreviewProfileVisibility] fetch error:', err);
      return { success: false, error: 'Network error' };
    }
  },

  // ── Admin Member Profile Editing ────────────────────────────────────────

  /**
   * Admin-only: edit permitted public profile fields for a real member.
   * Does NOT touch: passwords, auth credentials, private messages, likes,
   * matches, payments, or any user-only private data.
   */
  async adminEditMemberProfile(
    targetUserId: string,
    adminId: string,
    updates: Partial<{
      displayName: string;
      age: number;
      gender: string;
      usState: string;
      kenyanCounty: string;
      intention: string;
      faith: string;
      bio: string;
      interests: string[];
      profileStatus: string;
    }>
  ): Promise<{ success: boolean; error?: string }> {
    const supabase = createClient();
    const dbUpdates: Record<string, unknown> = {
      admin_last_edited_at: new Date().toISOString(),
      admin_last_edited_by: adminId,
      updated_at: new Date().toISOString(),
    };
    if (updates.displayName !== undefined) dbUpdates.display_name = updates.displayName;
    if (updates.age !== undefined) dbUpdates.age = updates.age;
    if (updates.gender !== undefined) dbUpdates.gender = updates.gender;
    if (updates.usState !== undefined) dbUpdates.us_state = updates.usState;
    if (updates.kenyanCounty !== undefined) dbUpdates.kenyan_county = updates.kenyanCounty;
    if (updates.intention !== undefined) dbUpdates.intention = updates.intention;
    if (updates.faith !== undefined) dbUpdates.faith = updates.faith;
    if (updates.bio !== undefined) dbUpdates.bio = updates.bio;
    if (updates.interests !== undefined) dbUpdates.interests = updates.interests;
    if (updates.profileStatus !== undefined) dbUpdates.profile_status = updates.profileStatus;

    const { error } = await supabase
      .from('member_profiles')
      .update(dbUpdates)
      .eq('user_id', targetUserId);

    if (error) {
      console.error('[adminEditMemberProfile] error:', error.message);
      return { success: false, error: error.message };
    }

    // Sync display_name to user_profiles if changed
    if (updates.displayName) {
      const initials = updates.displayName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
      await supabase.from('user_profiles').update({
        display_name: updates.displayName,
        avatar_initials: initials,
      }).eq('id', targetUserId);
      await supabase.from('member_profiles').update({
        avatar_initials: initials,
      }).eq('user_id', targetUserId);
    }

    await jsnService.addAuditLog(
      adminId,
      targetUserId,
      'admin_profile_edit',
      `Admin edited public profile fields: ${Object.keys(updates).join(', ')}`
    );

    return { success: true };
  },

  /**
   * Admin-only: upload/replace a real member's public profile photo. * Sets photo_status to'pending' so Admin must explicitly approve it.
   * Does NOT auto-approve — Admin reviews before making it publicly visible.
   *
   * Flow: base64 → Blob → Storage upload (unique path) → public URL → DB update
   */
  async adminUploadMemberPhoto(
    targetUserId: string,
    adminId: string,
    photoDataUrl: string
  ): Promise<{ success: boolean; error?: string }> {
    const supabase = createClient();

    // ── Step 1: Convert base64 data URL → Blob ──────────────────────────
    let blob: Blob;
    try {
      blob = dataUrlToBlob(photoDataUrl);
      console.log('[adminUploadMemberPhoto] blob size:', blob.size, 'type:', blob.type);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Unknown error';
      console.error('[adminUploadMemberPhoto] Step 1 FAILED (blob conversion):', msg);
      return { success: false, error: `Photo conversion failed: ${msg}` };
    }

    // ── Step 2: Upload to Supabase Storage (unique path per upload) ──────
    const timestamp = Date.now();
    const storagePath = `${targetUserId}/admin-upload-${timestamp}.jpg`;
    const { error: uploadError } = await supabase.storage
      .from('profile-photos')
      .upload(storagePath, blob, {
        contentType: 'image/jpeg',
        upsert: true,
      });

    if (uploadError) {
      console.error('[adminUploadMemberPhoto] Step 2 FAILED (storage upload):', uploadError.message, uploadError);
      return { success: false, error: `Storage upload failed: ${uploadError.message}` };
    }
    console.log('[adminUploadMemberPhoto] Step 2 OK — uploaded to:', storagePath);

    // ── Step 3: Get public URL ────────────────────────────────────────────
    const { data: urlData } = supabase.storage
      .from('profile-photos')
      .getPublicUrl(storagePath);

    const publicUrl = urlData?.publicUrl;
    if (!publicUrl) {
      console.error('[adminUploadMemberPhoto] Step 3 FAILED (no public URL returned)');
      return { success: false, error: 'Could not retrieve public URL for uploaded photo.' };
    }
    console.log('[adminUploadMemberPhoto] Step 3 OK — public URL:', publicUrl);

    // ── Step 4: Update member_profiles row ───────────────────────────────
    const { error: dbError } = await supabase
      .from('member_profiles')
      .update({
        photo_url: publicUrl,
        photo_status: 'pending',
        photo_rejection_reason: null,
        admin_last_edited_at: new Date().toISOString(),
        admin_last_edited_by: adminId,
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', targetUserId);

    if (dbError) {
      console.error('[adminUploadMemberPhoto] Step 4 FAILED (DB update):', dbError.message, dbError);
      return { success: false, error: `Database update failed: ${dbError.message}` };
    }
    console.log('[adminUploadMemberPhoto] Step 4 OK — member_profiles updated');

    // ── Step 5: Audit log ─────────────────────────────────────────────────
    try {
      await jsnService.addAuditLog(
        adminId,
        targetUserId,
        'admin_photo_upload',
        'Admin uploaded/replaced member profile photo — set to Pending Review'
      );
      console.log('[adminUploadMemberPhoto] Step 5 OK — audit log written');
    } catch (auditErr) {
      console.warn('[adminUploadMemberPhoto] Step 5 WARN (audit log failed, non-fatal):', auditErr);
    }

    return { success: true };
  },

  /**
   * Admin-only: remediate a legacy profile photo on behalf of an approved member.
   * Sets photo_status to 'approved' immediately — no pending review required.
   * This is a trusted admin action for one-time cleanup of pre-cropper photos.
   *
   * Flow: base64 → Blob → Storage upload (unique path) → public URL → DB update
   * Each step is logged individually so the exact failure point is visible.
   */
  async adminRemediatePhoto(
    targetUserId: string,
    adminId: string,
    photoDataUrl: string
  ): Promise<{ success: boolean; publicUrl?: string; error?: string }> {
    const supabase = createClient();

    // ── Step 1: Convert base64 data URL → Blob ──────────────────────────
    let blob: Blob;
    try {
      blob = dataUrlToBlob(photoDataUrl);
      console.log('[adminRemediatePhoto] blob size:', blob.size, 'type:', blob.type);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Unknown error';
      console.error('[adminRemediatePhoto] Step 1 FAILED (blob conversion):', msg);
      return { success: false, error: `Photo conversion failed: ${msg}` };
    }

    // ── Step 2: Upload to Supabase Storage (unique path per remediation) ─
    // Using a timestamp suffix ensures each remediation creates a new file,
    // preventing any caching or overwrite conflicts on repeated saves.
    const timestamp = Date.now();
    const storagePath = `${targetUserId}/remediation-${timestamp}.jpg`;
    const { error: uploadError } = await supabase.storage
      .from('profile-photos')
      .upload(storagePath, blob, {
        contentType: 'image/jpeg',
        upsert: true,
      });

    if (uploadError) {
      console.error('[adminRemediatePhoto] Step 2 FAILED (storage upload):', uploadError.message, uploadError);
      return { success: false, error: `Storage upload failed: ${uploadError.message}` };
    }
    console.log('[adminRemediatePhoto] Step 2 OK — uploaded to:', storagePath);

    // ── Step 3: Get public URL ────────────────────────────────────────────
    const { data: urlData } = supabase.storage
      .from('profile-photos')
      .getPublicUrl(storagePath);

    const publicUrl = urlData?.publicUrl;
    if (!publicUrl) {
      console.error('[adminRemediatePhoto] Step 3 FAILED (no public URL returned)');
      return { success: false, error: 'Could not retrieve public URL for uploaded photo.' };
    }
    console.log('[adminRemediatePhoto] Step 3 OK — public URL:', publicUrl);

    // ── Step 4: Update member_profiles row ───────────────────────────────
    const { error: dbError } = await supabase
      .from('member_profiles')
      .update({
        photo_url: publicUrl,
        photo_status: 'approved',
        photo_rejection_reason: null,
        admin_last_edited_at: new Date().toISOString(),
        admin_last_edited_by: adminId,
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', targetUserId);

    if (dbError) {
      console.error('[adminRemediatePhoto] Step 4 FAILED (DB update):', dbError.message, dbError);
      return { success: false, error: `Database update failed: ${dbError.message}` };
    }
    console.log('[adminRemediatePhoto] Step 4 OK — member_profiles updated as approved');

    // ── Step 5: Audit log ─────────────────────────────────────────────────
    try {
      await jsnService.addAuditLog(
        adminId,
        targetUserId,
        'admin_photo_remediation',
        'Admin replaced and cropped legacy profile photo — saved as Approved (remediation action)'
      );
      console.log('[adminRemediatePhoto] Step 5 OK — audit log written');
    } catch (auditErr) {
      console.warn('[adminRemediatePhoto] Step 5 WARN (audit log failed, non-fatal):', auditErr);
    }

    // ── Step 6: Clear Discover cache ──────────────────────────────────────
    try {
      const { clearDiscoverCache } = await import('@/lib/discoverPrefetch');
      clearDiscoverCache();
      console.log('[adminRemediatePhoto] Step 6 OK — Discover cache cleared');
    } catch {
      console.warn('[adminRemediatePhoto] Step 6 WARN (cache clear failed, non-fatal)');
    }

    return { success: true, publicUrl };
  },

  /**
   * Admin-only: get a single member profile by userId (for the edit modal).
   */
  async getMemberProfileByUserId(userId: string): Promise<MemberProfile | null> {
    const supabase = createClient();
    const { data } = await supabase
      .from('member_profiles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    return data ? toMemberProfile(data as Record<string, unknown>) : null;
  },

  /**
   * Admin-only: remediate a demo (preview) profile photo.
   * Mirrors adminRemediatePhoto exactly — same blob conversion, storage upload,
   * timestamped path, public URL retrieval, and audit log pattern.
   * Updates preview_profiles.photo_url directly — no pending/approval step needed.
   *
   * Flow: base64 → Blob → Storage upload (unique path) → public URL → DB update
   * Each step is logged individually so the exact failure point is visible.
   */
  async adminRemediateDemoPhoto(
    previewProfileId: string,
    adminId: string,
    photoDataUrl: string
  ): Promise<{ success: boolean; publicUrl?: string; error?: string }> {
    const supabase = createClient();

    // ── Step 1: Convert base64 data URL → Blob ──────────────────────────
    let blob: Blob;
    try {
      blob = dataUrlToBlob(photoDataUrl);
      console.log('[adminRemediateDemoPhoto] blob size:', blob.size, 'type:', blob.type);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Unknown error';
      console.error('[adminRemediateDemoPhoto] Step 1 FAILED (blob conversion):', msg);
      return { success: false, error: `Photo conversion failed: ${msg}` };
    }

    // ── Step 2: Upload to Supabase Storage (unique path per remediation) ─
    // Using a timestamp suffix ensures each remediation creates a new file,
    // preventing any caching or overwrite conflicts on repeated saves.
    const timestamp = Date.now();
    const storagePath = `demo-profiles/${previewProfileId}/remediation-${timestamp}.jpg`;
    const { error: uploadError } = await supabase.storage
      .from('profile-photos')
      .upload(storagePath, blob, {
        contentType: 'image/jpeg',
        upsert: true,
      });

    if (uploadError) {
      console.error('[adminRemediateDemoPhoto] Step 2 FAILED (storage upload):', uploadError.message, uploadError);
      return { success: false, error: `Storage upload failed: ${uploadError.message}` };
    }
    console.log('[adminRemediateDemoPhoto] Step 2 OK — uploaded to:', storagePath);

    // ── Step 3: Get public URL ────────────────────────────────────────────
    const { data: urlData } = supabase.storage
      .from('profile-photos')
      .getPublicUrl(storagePath);

    const publicUrl = urlData?.publicUrl;
    if (!publicUrl) {
      console.error('[adminRemediateDemoPhoto] Step 3 FAILED (no public URL returned)');
      return { success: false, error: 'Could not retrieve public URL for uploaded photo.' };
    }
    console.log('[adminRemediateDemoPhoto] Step 3 OK — public URL:', publicUrl);

    // ── Step 4: Update preview_profiles row ──────────────────────────────
    const { error: dbError } = await supabase
      .from('preview_profiles')
      .update({
        photo_url: publicUrl,
        updated_at: new Date().toISOString(),
      })
      .eq('id', previewProfileId);

    if (dbError) {
      console.error('[adminRemediateDemoPhoto] Step 4 FAILED (DB update):', dbError.message, dbError);
      return { success: false, error: `Database update failed: ${dbError.message}` };
    }
    console.log('[adminRemediateDemoPhoto] Step 4 OK — preview_profiles updated');

    // ── Step 5: Audit log ─────────────────────────────────────────────────
    // target_user_id is nullable in admin_audit_log — pass null for demo profiles
    // since they have no corresponding user_profiles row.
    try {
      const supabaseAudit = createClient();
      await supabaseAudit.from('admin_audit_log').insert({
        admin_user_id: adminId,
        target_user_id: null,
        action: 'admin_demo_photo_remediation',
        details: `Admin replaced and cropped demo profile photo (preview_profile_id: ${previewProfileId}) — saved immediately (no review required)`,
      });
      console.log('[adminRemediateDemoPhoto] Step 5 OK — audit log written');
    } catch (auditErr) {
      console.warn('[adminRemediateDemoPhoto] Step 5 WARN (audit log failed, non-fatal):', auditErr);
    }

    // ── Step 6: Clear Discover cache ──────────────────────────────────────
    try {
      const { clearDiscoverCache } = await import('@/lib/discoverPrefetch');
      clearDiscoverCache();
      console.log('[adminRemediateDemoPhoto] Step 6 OK — Discover cache cleared');
    } catch {
      console.warn('[adminRemediateDemoPhoto] Step 6 WARN (cache clear failed, non-fatal)');
    }

    return { success: true, publicUrl };
  },

  // ── Issue Reports ────────────────────────────────────────────────────────

  async getAllIssueReports(): Promise<IssueReport[]> {
    const supabase = createClient();
    const { data } = await supabase
      .from('issue_reports')
      .select('*')
      .order('created_at', { ascending: false });
    return (data || []).map(toIssueReport);
  },

  async updateIssueReportStatus(
    reportId: string,
    status: string,
    adminNotes?: string
  ): Promise<{ success: boolean; error?: string }> {
    const supabase = createClient();
    const updates: Record<string, unknown> = { status, updated_at: new Date().toISOString() };
    if (adminNotes !== undefined) updates.admin_notes = adminNotes;
    const { error } = await supabase.from('issue_reports').update(updates).eq('id', reportId);
    if (error) return { success: false, error: error.message };
    return { success: true };
  },
};
